import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

const Signup = () => {
	const navigate = useNavigate();
	const { signup } = useContext(AuthContext);
	const [formData, setFormData] = useState({
		fullName: '',
		email: '',
		password: '',
		confirmPassword: '',
	});
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData(prev => ({ ...prev, [name]: value }));
		setError('');
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError('');
		
		// Validation
		if (formData.password !== formData.confirmPassword) {
			setError('Passwords do not match');
			return;
		}

		if (formData.password.length < 6) {
			setError('Password must be at least 6 characters');
			return;
		}

		setIsLoading(true);
		try {
			// Extract username from email (before @)
			const username = formData.email.split('@')[0].toLowerCase();
			
			const result = await signup({
				username: username,
				full_name: formData.fullName,
				email: formData.email,
				password: formData.password,
				password_confirmation: formData.confirmPassword,
			});

			if (result.success) {
				// Navigate to home on success
				navigate('/');
			} else {
				// Display validation errors if any
				if (result.errors) {
					const errorMessages = Object.values(result.errors).flat().join(', ');
					setError(errorMessages);
				} else {
					setError(result.message || 'Registration failed. Please try again.');
				}
			}
		} catch (err) {
			console.error('Signup error:', err);
			setError('An unexpected error occurred. Please try again.');
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
			<div className="w-full max-w-md space-y-8">
				{/* Logo and heading */}
				<div className="text-center">
					<img
						src="/images/casa-gatchalian-logo.jpg"
						alt="Casa Gatchalian"
						className="h-16 w-16 rounded-full mx-auto mb-4 shadow-md"
					/>
					<h2 className="mt-6 text-3xl font-bold text-gray-900">Create Account</h2>
					<p className="mt-2 text-sm text-gray-600">Join us and start booking your perfect stay</p>
				</div>

				{/* Error message */}
				{error && (
					<div className="rounded-md bg-red-50 p-4 border border-red-200">
						<div className="flex">
							<svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
								<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
							</svg>
							<div className="ml-3">
								<p className="text-sm text-red-800">{error}</p>
							</div>
						</div>
					</div>
				)}

				{/* Form */}
				<form onSubmit={handleSubmit} className="space-y-6 bg-white p-8 rounded-lg shadow-md">
					{/* Full Name */}
					<div>
						<label htmlFor="fullName" className="block text-sm font-medium text-gray-700">Full Name</label>
						<input
							type="text"
							name="fullName"
							id="fullName"
							placeholder="John Doe"
							value={formData.fullName}
							onChange={handleChange}
							required
							className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0f4c81] focus:border-transparent text-sm"
						/>
					</div>

					{/* Email */}
					<div>
						<label htmlFor="email" className="block text-sm font-medium text-gray-700">Email Address</label>
						<input
							type="email"
							name="email"
							id="email"
							placeholder="you@example.com"
							value={formData.email}
							onChange={handleChange}
							required
							className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0f4c81] focus:border-transparent text-sm"
						/>
					</div>

					{/* Password */}
					<div>
						<label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
						<input
							type="password"
							name="password"
							id="password"
							placeholder="At least 6 characters"
							value={formData.password}
							onChange={handleChange}
							required
							className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0f4c81] focus:border-transparent text-sm"
						/>
					</div>

					{/* Confirm Password */}
					<div>
						<label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">Confirm Password</label>
						<input
							type="password"
							name="confirmPassword"
							id="confirmPassword"
							placeholder="Re-enter your password"
							value={formData.confirmPassword}
							onChange={handleChange}
							required
							className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0f4c81] focus:border-transparent text-sm"
						/>
					</div>

					{/* Terms checkbox */}
					<div className="flex items-center">
						<input
							type="checkbox"
							name="terms"
							id="terms"
							required
							className="h-4 w-4 text-[#0f4c81] focus:ring-[#0f4c81] border-gray-300 rounded"
						/>
						<label htmlFor="terms" className="ml-2 block text-sm text-gray-700">
							I agree to the <a href="#" className="text-[#0f4c81] hover:underline font-medium">Terms & Conditions</a> and <a href="#" className="text-[#0f4c81] hover:underline font-medium">Privacy Policy</a>
						</label>
					</div>

					{/* Sign up button */}
					<button
						type="submit"
						disabled={isLoading}
						className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-semibold text-white bg-[#0f4c81] hover:bg-[#0d3b66] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0f4c81]"
					>
						{isLoading ? 'Creating Account...' : 'Sign Up'}
					</button>
				</form>

				{/* Login link */}
				<div className="text-center">
					<span className="text-sm text-gray-600">Already have an account? </span>
					<button
						type="button"
						onClick={() => navigate('/login')}
						className="font-medium text-[#0f4c81] hover:underline"
					>
						Log in
					</button>
				</div>
			</div>
		</div>
	);
};

export default Signup;
